import React from "react"

export default function ModalSyncPreview({ syncPreview, formatPointValue, formatEdgeLabel }) {
  const edgeDetails = (edge) => {
    if (!edge) return '-'
    return `avoid: ${edge.avoid ? 'sim' : 'não'} | reversível: ${edge.reversable ? 'sim' : 'não'}`
  }

  const areaDetails = (area) => {
    if (!area) return '-'
    return `vel: ${area.velocity} | beep: ${area.beep ? 'sim' : 'não'}`
  }

  const polygonDetails = (zone) => {
    const polygon = Array.isArray(zone?.polygon) ? zone.polygon : []
    if (polygon.length === 0) return '-'
    return polygon.map((corner) => `(${corner.point?.x}, ${corner.point?.y})`).join(' ')
  }

  const edgeRows = [
    ...(syncPreview?.edges?.added || []).map((after) => ({ type: 'added', before: null, after })),
    ...(syncPreview?.edges?.removed || []).map((before) => ({ type: 'removed', before, after: null })),
    ...(syncPreview?.edges?.changed || []).map(({ before, after }) => ({ type: 'updated', before, after }))
  ]

  const areaRows = [
    ...(syncPreview?.areas?.added || []).map((after) => ({ type: 'added', before: null, after })),
    ...(syncPreview?.areas?.removed || []).map((before) => ({ type: 'removed', before, after: null })),
    ...(syncPreview?.areas?.changed || []).map(({ before, after }) => ({ type: 'updated', before, after }))
  ]

  const noGoRows = [
    ...(syncPreview?.noGo?.added || []).map((after) => ({ type: 'added', before: null, after })),
    ...(syncPreview?.noGo?.removed || []).map((before) => ({ type: 'removed', before, after: null }))
  ]

  const rowClassByType = (type) => (
    type === 'added' ? 'has-background-success-light'
      : type === 'removed' ? 'has-background-danger-light'
        : 'has-background-warning-light'
  )

  const tagClassByType = (type) => (
    type === 'added' ? 'tag is-success'
      : type === 'removed' ? 'tag is-danger'
        : 'tag is-warning'
  )

  const typeLabel = (type) => (
    type === 'added' ? 'Adicionado'
      : type === 'removed' ? 'Removido'
        : 'Atualizado'
  )
  
  if (!syncPreview) return <p>Preparando preview...</p>

  return (
    <div className="content">
      <p className="has-text-weight-semibold">
        Revise as mudanças que serão enviadas para o FMS. Confirme para continuar.
      </p>

      <h5 className="title is-6 mt-4">Pontos (Points)</h5>
      {syncPreview?.points?.length > 0 ? (
        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
          <table className="table is-fullwidth is-striped is-narrow">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Antes (x,y)</th>
                <th>Depois (x,y)</th>
                <th>Tipo</th>
              </tr>
            </thead>
            <tbody>
              {syncPreview.points.map((item, idx) => (
                <tr key={`${item.name}-${idx}`} className={rowClassByType(item.type)}>
                  <td>{item.name}</td>
                  <td>{formatPointValue(item.before)}</td>
                  <td>{formatPointValue(item.after)}</td>
                  <td>
                    <span className={tagClassByType(item.type)}>{typeLabel(item.type)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="has-text-grey-light">Nenhuma mudança nos pontos detectada.</p>
      )}

      <h5 className="title is-6 mt-4">Conexões (Edges)</h5>
      {edgeRows.length > 0 ? (
        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
          <table className="table is-fullwidth is-striped is-narrow is-size-7">
            <thead>
              <tr>
                <th>Conexão</th>
                <th>Antes</th>
                <th>Depois</th>
                <th>Tipo</th>
              </tr>
            </thead>
            <tbody>
              {edgeRows.map((row, idx) => (
                <tr key={`edge-${idx}`} className={rowClassByType(row.type)}>
                  <td>{formatEdgeLabel(row.after || row.before)}</td>
                  <td>{edgeDetails(row.before)}</td>
                  <td>{edgeDetails(row.after)}</td>
                  <td>
                    <span className={tagClassByType(row.type)}>{typeLabel(row.type)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="has-text-grey-light">Nenhuma mudança nas conexões detectada.</p>
      )}

      <h5 className="title is-6 mt-4">Zonas (Areas)</h5>
      {areaRows.length > 0 ? (
        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
          <table className="table is-fullwidth is-striped is-narrow is-size-7">
            <thead>
              <tr>
                <th>Zona</th>
                <th>Antes</th>
                <th>Depois</th>
                <th>Tipo</th>
              </tr>
            </thead>
            <tbody>
              {areaRows.map((row, idx) => (
                <tr key={`area-${idx}`} className={rowClassByType(row.type)}>
                  <td>{row.after?.name || row.before?.name || '-'}</td>
                  <td>{areaDetails(row.before)}</td>
                  <td>{areaDetails(row.after)}</td>
                  <td>
                    <span className={tagClassByType(row.type)}>{typeLabel(row.type)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="has-text-grey-light">Nenhuma mudança nas zonas detectada.</p>
      )}

      <h5 className="title is-6 mt-4">Zonas Proibidas (No-go Zones)</h5>
      {noGoRows.length > 0 ? (
        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
          <table className="table is-fullwidth is-striped is-narrow is-size-7">
            <thead>
              <tr>
                <th>Polígono</th>
                <th>Tipo</th>
              </tr>
            </thead>
            <tbody>
              {noGoRows.map((row, idx) => (
                <tr key={`nogo-${idx}`} className={rowClassByType(row.type)}>
                  <td>{polygonDetails(row.after || row.before)}</td>
                  <td>
                    <span className={tagClassByType(row.type)}>{typeLabel(row.type)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="has-text-grey-light">Nenhuma mudança nas zonas proibidas detectada.</p>
      )}
    </div>
  )
}
